/**
 * src/lib/api/resumableUploadStore.js — TICKET-MINIO-RESUMABLE Slice 3 (CEO 2026-06-01).
 *
 * Resume-on-reopen persistence for large multipart uploads. iOS Safari cannot
 * upload in the background (no Background Fetch API) and freezes a backgrounded
 * page — so the achievable best is: persist the multipart session + the raw file
 * bytes to IndexedDB as parts complete, and on the next app open re-PUT only the
 * MISSING parts (via uploadResumable's resume contract) and finish.
 *
 * ISOLATION: this uses its OWN IndexedDB database (`acn-resumable-uploads`),
 * deliberately NOT the breadcrumb queue DB (`acn-voice-queue`). The breadcrumb
 * queue is hot, version-2, and shared by the live-recording path; bumping its
 * schema for an unrelated feature would run an upgrade for every user. A separate
 * DB keeps that critical store untouched.
 *
 * DORMANT until Slice 3b wires uploadVoiceFile's onState into saveUpload() and an
 * app-start hook into resumeIncompleteUploads(). Nothing imports it yet → zero
 * prod change. Fully unit-tested with fake-indexeddb + injected fetch/XHR.
 *
 * Plan: docs/plans/2026-06-01-resumable-upload-plan.md
 */
import { uploadResumable } from './resumableUpload';

const DB_NAME = 'acn-resumable-uploads';
const DB_VERSION = 1;
const STORE = 'uploads';

let dbPromise = null;

export function openUploadDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' });
        store.createIndex('status', 'status', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

export async function _closeUploadDB() {
  if (dbPromise) {
    try { (await dbPromise).close(); } catch { /* spine-emit-ok: db may not have opened */ }
  }
  dbPromise = null;
}

export function _resetUploadDB() { dbPromise = null; }

/**
 * Insert or replace an upload record. The record carries enough to resume after
 * a full reload: the scope, the file metadata, the raw file Blob, and the
 * uploadResumable session ({uploadId, objectKey, partSize, completedParts}).
 */
export async function saveUpload(record) {
  if (!record || !record.key) throw new Error('saveUpload: key is required');
  const db = await openUploadDB();
  const row = {
    status: 'uploading',
    error: null,
    createdAt: record.createdAt || Date.now(),
    ...record,
    updatedAt: Date.now(),
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).put(row);
    req.onsuccess = () => resolve(row);
    req.onerror = () => reject(req.error);
  });
}

export async function getUpload(key) {
  const db = await openUploadDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

/** Merge updates into an existing record. No-op (resolves null) if absent. */
export async function patchUpload(key, updates) {
  const db = await openUploadDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const getReq = store.get(key);
    getReq.onsuccess = () => {
      const existing = getReq.result;
      if (!existing) { resolve(null); return; }
      const merged = { ...existing, ...updates, updatedAt: Date.now() };
      const putReq = store.put(merged);
      putReq.onsuccess = () => resolve(merged);
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

export async function deleteUpload(key) {
  const db = await openUploadDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function getAllUploads() {
  const db = await openUploadDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

/** Uploads that have not yet completed (resume candidates). */
export async function getIncompleteUploads() {
  const all = await getAllUploads();
  return all.filter((u) => u.status !== 'complete');
}

/**
 * Rebuild a File-like object for uploadResumable from a persisted Blob. On
 * resume the original File handle is gone (page reloaded), so the bytes can only
 * come from the persisted Blob. uploadResumable needs `.size` + `.slice`; the
 * server-minted objectKey lives in the session so `.name` is never re-read.
 *
 * `knownSize` (the persisted `fileSize` number) is authoritative for `.size` —
 * a plain number always survives the IDB structured-clone roundtrip, whereas the
 * Blob's own `.size`/`.slice` can be stripped (jsdom + fake-indexeddb always; real
 * Safari occasionally after an IDB upgrade). Real browsers preserve the Blob bytes,
 * so `.slice` works in production; we rehydrate a fresh Blob only as a safety net.
 */
function fileLikeFromBlob(blob, fileName, mimeType, knownSize) {
  const size = (Number.isFinite(knownSize) && knownSize > 0)
    ? knownSize
    : (blob && typeof blob.size === 'number' ? blob.size : NaN);
  if (!(size > 0)) {
    throw new Error('resumable resume: persisted file size is unknown — cannot resume');
  }
  // Prefer the native Blob.slice (production path); rehydrate into a fresh Blob if
  // the prototype was stripped. Mirrors voiceQueue.getRecordingBlob's reconstruction.
  const sliceable = (blob && typeof blob.slice === 'function')
    ? blob
    : new Blob([blob ?? new Uint8Array(0)], { type: mimeType || (blob && blob.type) || '' });
  return { name: fileName, type: mimeType, size, slice: (s, e) => sliceable.slice(s, e) };
}

/**
 * Resume every incomplete upload found in the store. For each: re-PUT only the
 * missing parts (uploadResumable reuses the persisted session), persist progress
 * back to the store after each part, then hand the completed object to `finalize`
 * (the caller's voice.register_upload + process step) and delete the record. A
 * failure marks the record 'failed' (kept for a later retry), never deletes it.
 *
 * `finalize({ key, objectKey, storageBackend, scopeType, scopeId, fileName, fileSize, mimeType })`
 * is injected so this module stays decoupled from voice-v2 (no circular import).
 *
 * @returns {Promise<{resumed:number, completed:number, failed:number}>}
 */
export async function resumeIncompleteUploads({
  finalize,
  apiFetchImpl,
  xhrImpl,
  onProgress,
  uploadResumableImpl = uploadResumable,
  maxAgeMs = 7 * 24 * 60 * 60 * 1000, // skip records older than 7 days (likely abandoned)
} = {}) {
  const candidates = await getIncompleteUploads();
  const now = Date.now();
  let resumed = 0; let completed = 0; let failed = 0;

  for (const rec of candidates) {
    if (maxAgeMs && rec.createdAt && (now - rec.createdAt) > maxAgeMs) continue;
    resumed += 1;
    try {
      const file = fileLikeFromBlob(rec.fileBlob, rec.fileName, rec.mimeType, rec.fileSize);
      const result = await uploadResumableImpl({
        file,
        scopeType: rec.scopeType,
        scopeId: rec.scopeId,
        state: rec.session || null,
        apiFetchImpl,
        xhrImpl,
        onState: (session) => patchUpload(rec.key, { session }),
        onProgress: onProgress ? (loaded, total) => onProgress(rec.key, loaded, total) : undefined,
      });
      if (typeof finalize === 'function') {
        await finalize({
          key: rec.key,
          objectKey: result.object_key,
          storageBackend: result.storage_backend,
          scopeType: rec.scopeType,
          scopeId: rec.scopeId,
          fileName: rec.fileName,
          fileSize: rec.fileSize,
          mimeType: rec.mimeType,
        });
      }
      await deleteUpload(rec.key);
      completed += 1;
    } catch (err) {
      failed += 1;
      await patchUpload(rec.key, { status: 'failed', error: err?.message || String(err) });
    }
  }
  return { resumed, completed, failed };
}
